import type { DateRange } from "@gettactic/components/src/calendar/types";
import { format, isSameDay, isValid, parse } from "date-fns";
import type { VisitorTab } from "../views/VisitorsContext";

const DATE_FORMAT = "yyyy-MM-dd";

const VISITOR_TABS: VisitorTab[] = [
  "log",
  "upcoming",
  "preregistered",
  "visitors",
  "bulkImports"
];

export interface VisitorsUrlState {
  activeTab: VisitorTab;
  searchText: string;
  visitorTypeFilter: string;
  dateRange: DateRange;
  selectedHostId: string;
  selectedOfficeIds: string[];
}

export interface VisitorsUrlDefaults {
  today: Date;
  officeIds: string[];
}

type UrlQueryValue = string | number | Date;

export const isSameSet = (a: string[], b: string[]) =>
  a.length === b.length && a.every((id) => b.includes(id));

const toStringList = (
  value: UrlQueryValue | UrlQueryValue[] | undefined
): string[] => {
  if (value === undefined) return [];
  return (Array.isArray(value) ? value : [value]).map(String);
};

const toSingleString = (
  value: UrlQueryValue | UrlQueryValue[] | undefined
): string | undefined => toStringList(value)[0];

const parseDate = (value: string | undefined): Date | undefined => {
  if (!value) return undefined;
  const date = parse(value, DATE_FORMAT, new Date());
  return isValid(date) ? date : undefined;
};

/**
 * The Visitors page state as query params, leaving out whatever matches the
 * defaults (Visitor Log, today, all types and hosts, the user's offices) so a
 * plain visit keeps a bare URL.
 */
export const toVisitorsUrlQuery = (
  state: VisitorsUrlState,
  defaults: VisitorsUrlDefaults
): Record<string, string | string[] | null> => {
  const from = state.dateRange.from;
  const to = state.dateRange.to ?? from;
  const isDefaultRange =
    !!from &&
    !!to &&
    isSameDay(from, defaults.today) &&
    isSameDay(to, defaults.today);

  return {
    tab: state.activeTab === "log" ? null : state.activeTab,
    search: state.searchText || null,
    type:
      state.visitorTypeFilter && state.visitorTypeFilter !== "all"
        ? state.visitorTypeFilter
        : null,
    from: from && !isDefaultRange ? format(from, DATE_FORMAT) : null,
    to: to && !isDefaultRange ? format(to, DATE_FORMAT) : null,
    host: state.selectedHostId || null,
    offices: isSameSet(state.selectedOfficeIds, defaults.officeIds)
      ? null
      : state.selectedOfficeIds
  };
};

/**
 * The parts of the Visitors page state a URL describes. Unknown tabs and
 * malformed dates are ignored so a stale or hand-edited link still opens.
 */
export const fromVisitorsUrlQuery = (
  params: Record<string, UrlQueryValue | UrlQueryValue[]>
): Partial<VisitorsUrlState> => {
  const state: Partial<VisitorsUrlState> = {};

  const tab = toSingleString(params.tab);
  if (tab && VISITOR_TABS.includes(tab as VisitorTab)) {
    state.activeTab = tab as VisitorTab;
  }

  const search = toSingleString(params.search);
  if (search) state.searchText = search;

  const type = toSingleString(params.type);
  if (type) state.visitorTypeFilter = type;

  const from = parseDate(toSingleString(params.from));
  if (from) {
    const to = parseDate(toSingleString(params.to));
    state.dateRange = { from, to: to && to >= from ? to : from };
  }

  const host = toSingleString(params.host);
  if (host) state.selectedHostId = host;

  const offices = toStringList(params.offices);
  if (offices.length > 0) state.selectedOfficeIds = offices;

  return state;
};
